import { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { DownloadSimple, LinkSimple, Cards } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const TEMPLATES = [
  { key: "classic", name: "Classic Academic", bg: "#f6f2e9", fg: "#1a2e22", accent: "#7a5c2e", ring: "#c9a15e", font: "'Georgia', serif" },
  { key: "elegant", name: "Elegant Memorial", bg: "#ffffff", fg: "#2b2b2b", accent: "#b08d57", ring: "#d9c39a", font: "'Georgia', serif" },
  { key: "school", name: "School Heritage", bg: "#14392b", fg: "#f2efe6", accent: "#e8c86b", ring: "#e8c86b", font: "'Georgia', serif" },
  { key: "university", name: "University Heritage", bg: "#152238", fg: "#f4f1e8", accent: "#d4af5a", ring: "#d4af5a", font: "'Georgia', serif" },
  { key: "minimal", name: "Minimal Modern", bg: "#ffffff", fg: "#111111", accent: "#666666", ring: "#dddddd", font: "'Helvetica Neue', Arial, sans-serif" },
  { key: "alumni", name: "Alumni Community", bg: "#f3ede2", fg: "#26332b", accent: "#1a7a4c", ring: "#1a7a4c", font: "'Georgia', serif" },
  { key: "bw", name: "Black & White Historical", bg: "#1c1c1c", fg: "#f0f0f0", accent: "#bcbcbc", ring: "#8a8a8a", font: "'Georgia', serif", mono: true },
  { key: "soft", name: "Soft Contemporary", bg: "#eef1f0", fg: "#2c3a34", accent: "#5b8c7a", ring: "#a9c7ba", font: "'Georgia', serif" },
];

export default function MemorialCardDialog({ m }) {
  const [tpl, setTpl] = useState(TEMPLATES[0]);
  const [busy, setBusy] = useState(false);
  const cardRef = useRef(null);
  const years = [m.dob, m.dop].filter(Boolean).join(" – ");
  const sub = [m.class_batch || m.graduation_year, m.institution].filter(Boolean).join(" · ");

  const download = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true, skipFonts: true });
      const a = document.createElement("a");
      a.download = `memorial-${(m.name || "card").replace(/\s+/g, "-").toLowerCase()}.png`;
      a.href = dataUrl;
      a.click();
      toast.success("Memorial card downloaded");
    } catch { toast.error("Could not generate card"); } finally { setBusy(false); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Memorial link copied");
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-full gap-1.5" data-testid="memorial-card-btn"><Cards size={14} /> Create Memorial Card</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto" data-testid="memorial-card-dialog">
        <DialogHeader><DialogTitle>Memorial Card</DialogTitle></DialogHeader>

        <div className="flex flex-wrap gap-2" data-testid="card-templates">
          {TEMPLATES.map((t) => (
            <button key={t.key} onClick={() => setTpl(t)} data-testid={`card-tpl-${t.key}`}
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${tpl.key === t.key ? "border-secondary bg-secondary/10 text-secondary" : "border-border/60 text-muted-foreground hover:border-secondary/50"}`}>{t.name}</button>
          ))}
        </div>

        <div className="mt-4 flex justify-center">
          <div ref={cardRef} data-testid="memorial-card-preview"
            style={{ width: 380, background: tpl.bg, color: tpl.fg, fontFamily: tpl.font, padding: "36px 28px", textAlign: "center", borderRadius: 16 }}>
            <div style={{ fontSize: 12, letterSpacing: 3, textTransform: "uppercase", color: tpl.accent, marginBottom: 18 }}>In Loving Memory</div>
            <div style={{ width: 120, height: 120, margin: "0 auto 18px", borderRadius: "50%", overflow: "hidden", border: `3px solid ${tpl.ring}` }}>
              {m.photo_file_id
                ? <img src={fileUrl(m.photo_file_id)} alt={m.name} crossOrigin="anonymous" style={{ width: "100%", height: "100%", objectFit: "cover", filter: tpl.mono ? "grayscale(100%)" : "none" }} />
                : <div style={{ width: "100%", height: "100%", display: "grid", placeItems: "center", background: tpl.ring, color: tpl.bg, fontSize: 40 }}>{(m.name || "?")[0]}</div>}
            </div>
            <div style={{ fontSize: 26, fontWeight: 500, lineHeight: 1.2 }}>{m.name}</div>
            {years && <div style={{ fontSize: 15, marginTop: 6, opacity: 0.85 }}>{years}</div>}
            {(m.city || m.country) && <div style={{ fontSize: 12, marginTop: 4, opacity: 0.7 }}>{[m.city, m.country].filter(Boolean).join(", ")}</div>}
            {sub && <div style={{ fontSize: 13, marginTop: 10, color: tpl.accent }}>{sub}</div>}
            <div style={{ width: 44, height: 2, background: tpl.accent, margin: "16px auto" }} />
            <div style={{ fontSize: 14, fontStyle: "italic", opacity: 0.85, lineHeight: 1.5 }}>
              {m.epitaph ? `“${m.epitaph}”` : "Forever part of our story."}
            </div>
            <div style={{ fontSize: 10, letterSpacing: 1, textTransform: "uppercase", marginTop: 22, opacity: 0.55 }}>AlumniPulse™ · Remember · Honor · Preserve</div>
          </div>
        </div>

        <div className="mt-4 flex justify-center gap-2">
          <Button onClick={download} disabled={busy} className="rounded-full gap-1.5" data-testid="card-download-btn"><DownloadSimple size={15} /> {busy ? "Preparing…" : "Download"}</Button>
          <Button variant="outline" onClick={copyLink} className="rounded-full gap-1.5" data-testid="card-copylink-btn"><LinkSimple size={15} /> Copy link</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
